"use client";

import { PlatformAuth, Profile } from "@sinclair-digital/cabin-auth-library";
import React, { createContext, useContext, useEffect, useRef, useState } from "react";

export type PlatformAuthContextType = {
    platformAuth: PlatformAuth | null;
    userInfo: Profile | null;
    isAuthenticated: boolean;
};

const PlatformAuthContext = createContext<PlatformAuthContextType>({
    platformAuth: null,
    userInfo: null,
    isAuthenticated: false,
});

export default function useAuth() {
    return useContext(PlatformAuthContext);
}

export function PlatformAuthProvider({ children }: { children: React.ReactNode }) {
    const [auth, setAuth] = useState<PlatformAuth | null>(null);
    const [isAuthenticated, setIsAuthenticated] = useState<boolean>(false);
    const [user, setUser] = useState<Profile | null>(null);

    const authInitialized = useRef(false);

    useEffect(() => {
        if (typeof window === "undefined" || authInitialized.current) return;
        const currAuth = new PlatformAuth();
        setAuth(currAuth);

        (async () => {
            const isAuthed = await currAuth.isAuthenticated();
            setIsAuthenticated(isAuthed);
            if (!isAuthed) return;
            const user = await currAuth.getUser();
            setUser(user);
        })();

        authInitialized.current = true;
    }, []);

    return (
        <PlatformAuthContext.Provider
            value={{ platformAuth: auth, isAuthenticated, userInfo: user }}
        >
            {isAuthenticated && (
                <div id="user-profile" data-testid="user-profile">
                    <img
                        id="user-picture"
                        src={user?.picture}
                        alt="User profile picture."
                        width={50}
                        height={50}
                    />
                    <p id="username">{user?.username}</p>
                </div>
            )}
            {children}
        </PlatformAuthContext.Provider>
    );
}
